import {includes} from '@shared/helpers/collection'
import {isEmpty} from '@shared/helpers/lang'

import {initOrderSheetByStorage} from '@web/actions/orderSheetInfo'
import {FamilyPayStatement, GeneralSkip, PayMethod, Service} from '@web/core/constants'
import {getFamilyPayStatement} from '@web/helpers/policies'
import {payMethodPolicy, getDefaultNormalPayMethod, getDefaultPaymentMethod} from '@web/slices/paymentMethod/slice'
import makeSlice from '@web/slices/paymentMethod/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(initOrderSheetByStorage.fulfilled, (state, {payload}) => {
                const {data} = payload
                if (
                    data.paymentMethod.selected.payMethods.includes(PayMethod.RECHARGE_POINT) ||
                    state.selected.payMethods.includes(PayMethod.RECHARGE_POINT)
                ) {
                    // 결제수단 선택은 복원하지 않음
                    state.selected.payMethods = [
                        PayMethod.RECHARGE_POINT,
                        ...state.selected.payMethods.filter((payMethod) => payMethod !== PayMethod.RECHARGE_POINT),
                    ]
                }
            })
            .addCase(initOrderSheetByAPI.fulfilled, (state, {payload}) => {
                const {
                    payMethod,
                    products,
                    merchantsByMerchantNo,
                    verifyServerClientData,
                    availablePayMethodTypes,
                    naverMembership,
                    familyPay,
                    entry,
                } = payload

                const nextPayMethod = payMethodPolicy.getNextPayMethodByComplexPayment({
                    availablePayMethodTypes,
                    hasCardEasyPays: !isEmpty(payMethod.cardEasyPays),
                })

                const familyPayStatement = (() => {
                    if (!familyPay) {
                        return FamilyPayStatement.HIDE
                    } else if (familyPay.apiResultType !== 'SUCCESS') {
                        return FamilyPayStatement.API_FAIL
                    }
                    return getFamilyPayStatement({
                        memberPaymentInfo: {
                            isFamilyPayAllowed: familyPay.approvalPayment,
                            type: familyPay.familyPayPaymentType,
                            isPreApprovalPaymentAllowed: familyPay.preApprovalPayment,
                        },
                        role: familyPay.familyUserType,
                        entry,
                        availablePayMethodTypes,
                        naverPlusMembershipSubscribed: naverMembership.subscribed,
                        familyPaySubscribed: !!familyPay.familyUserType,
                        status: 'STABLE',
                    })
                })()

                const shouldFixDefaultPayMethod =
                    payMethod.defaultPayment?.payMethodType === PayMethod.FAMILY_PAY &&
                    includes(familyPayStatement, [FamilyPayStatement.HIDE, FamilyPayStatement.DISABLE])

                // FIXME: 디폴트 결제수단이 패밀리결제로 내려오면서 미노출/비활성화 되는 케이스에는 defaultPaymentMethod를 패밀리결제가 아닌 가능한 결제수단으로 임의 판단하여 설정
                // see) https://oss.fin.navercorp.com/Product-Planning/Order_Plan/issues/2744#issuecomment-2234313
                const defaultPaymentMethod = shouldFixDefaultPayMethod
                    ? nextPayMethod
                    : getDefaultPaymentMethod({
                          availablePayMethodTypes,
                          payMethodType: payMethod.defaultPayment?.payMethodType ?? nextPayMethod,
                          nextPayMethod,
                      })

                state.complex.availableService = true
                state.availableTypes = availablePayMethodTypes
                state.defaultPayMethod = nextPayMethod

                state.defaultNormalPayMethod = getDefaultNormalPayMethod({
                    availablePayMethodTypes,
                    recentPayMethodType: payMethod.recentPayMethodType,
                })

                state.recentPayMethod = payMethod.recentPayMethodType

                const canUseRechargePointFirst = payMethod.useRechargePoint && payMethod.recentPaymentExist

                // 복합결제 및 결제수단 선택 내용 확인 필요
                state.selected.payMethods = [
                    ...(canUseRechargePointFirst ? [PayMethod.RECHARGE_POINT] : []),
                    ...(defaultPaymentMethod && defaultPaymentMethod !== PayMethod.NONE ? [defaultPaymentMethod] : []),
                ]
                state.selected.prevPayMethod = defaultPaymentMethod
                state.selected.payNextMethod = GeneralSkip.VIRTUAL_ACCOUNT
                state.statements = payMethod.payMethodStatement.payMethodStatementsByExposureAreaType
                state.errorExposureArea = payMethod.payMethodStatement.exposureAreaTypes

                const productItem = products[0] ?? {
                    billingCpId: '',
                    billingItemId: '',
                    productName: '',
                }
                const sellerId = Object.keys(merchantsByMerchantNo).reduce<string>((acc, merchantNo) => {
                    if (!acc) {
                        return `${merchantNo}`
                    }
                    return acc
                }, '')
                state.shopBill = {
                    resultType: payMethod.billingPayMethods.resultType,
                    authToken: payMethod.authToken,
                    /**
                     * @ref https://oss.fin.navercorp.com/shop-bill/sbill-js-sdk/wiki/SDK-%EB%AA%A8%EB%93%88---%EC%9B%B9%EC%9D%B8%EC%A6%9D-%EC%9A%94%EC%B2%AD
                     * @ref [주문서 서비스 타입 전달 논의](https://oss.fin.navercorp.com/payment-fe/order-web/issues/1691)
                     */
                    svcType: Service.C1,
                    sellerId,
                    cpId: productItem.billingCpId,
                    itemId: productItem.billingItemId,
                    title: productItem.productName,
                }
                state.verifyServerClientData = verifyServerClientData
            })
    },
})

export default slice.reducer
